import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TopVakanciesSearch from "../../topvakancies/topvacanciessearch";
import filtericon from "../../assets/icons/filter.png";
import {
  faLocationDot,
  faMoneyBill1,
  faCalendarAlt,
  faSearch,
  faHeart,
  faBars,
  faTimes,
  faXmark,
  faAngleLeft,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../components/modal/modal";
import { useLayoutEffect } from "react";
import SearchPageBanners from "../../components/searchPageBanners/searchpagebanners";

export const useLockBodyScroll = (isLocked) => {
  useLayoutEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    if (isLocked) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalStyle;
    }
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [isLocked]);
};

const SearchResults = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const userId = user?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [likedVacancies, setLikedVacancies] = useState([]);
  const query = searchParams.get("q")?.trim() || "";
  const categoryParam = searchParams.get("category") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const filtresObj = t("filtres", { returnObjects: true });
  const filtres = Array.isArray(filtresObj)
    ? filtresObj
    : Object.values(filtresObj || {});

  const [inputValue, setInputValue] = useState(query);
  const regionsData = t("regions", { returnObjects: true }) || {};
  const type = t("type", { returnObjects: true }) || {};
  const regions = Object.entries(regionsData)
    .filter(([key]) => key !== "selecttitle")
    .map(([_, value]) => value);

  const regionsTitle = regionsData.selecttitle || t("selecttitle");
  useLockBodyScroll(showMobileFilters);

  const [modalMessage, setModalMessage] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setShowMobileFilters(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!userId) return;
    const fetchLikes = async () => {
      try {
        const res = await fetch(
          `/api/get_liked_vacancies.php?user_id=${userId}`,
        );
        const data = await res.json();
        setLikedVacancies(
          Array.isArray(data)
            ? data.map((v) => Number(v.id))
            : data.liked?.map((v) => Number(v.id)) || [],
        );
      } catch (err) {
        console.error("Ошибка при получении лайков:", err);
      }
    };
    fetchLikes();
  }, [userId]);

  useEffect(() => {
    const pending = localStorage.getItem("pending_like");
    if (user && pending) {
      handleLike(parseInt(pending));
      localStorage.removeItem("pending_like");
    }
  }, [user]);

  const handleLike = async (vacancyId) => {
    const numericId = Number(vacancyId);
    // Проверяем, что это не компания
    if (!user || user.role === "company") {
      setModalMessage({
        title: t("attention"),
        ok: t("ok"),
        text: t("pleaseregister"),
        success: true,
        onClose: () => navigate("/WorkerChoose"),
      });
      if (!user) {
        localStorage.setItem("pending_like", numericId);
      }
      return;
    }

    try {
      const response = await fetch("/api/toggle_like.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          vacancy_id: numericId,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setLikedVacancies((prev) =>
          data.liked
            ? [...prev, numericId]
            : prev.filter((id) => id !== numericId),
        );
      }
    } catch (error) {
      console.error("Ошибка при лайке:", error);
    }
  };

  useEffect(() => {
    if (categoryParam) {
      const categories = categoryParam.split(",").filter(Boolean);
      setSelectedCategories(categories);
    } else {
      setSelectedCategories([]);
    }
  }, [categoryParam]);

  useEffect(() => {
    if (/^\d+$/.test(query)) {
      navigate(`/vacancy/${query}`);
    }
  }, [query, navigate]);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);

        let url = "";
        const topVacancies = t("topvacansies");
        const highestPaying = t("Highestpayingjobs");
        const lastVacancies = t("lastvacansies");

        // Проверяем специальные категории
        if (selectedCategories.includes(highestPaying)) {
          url = `/api/get_highest_salary_vacancies.php?page=${page}&limit=20&lang=${i18n.language}`;
        } else if (selectedCategories.includes(lastVacancies)) {
          url = `/api/get_all_vacancies.php?page=${page}&limit=20&lang=${i18n.language}`;
        } else if (selectedCategories.includes(topVacancies)) {
          url = `/api/search_vacancies.php?page=${page}&limit=20&lang=${i18n.language}&orderBy=views DESC`;
        } else {
          url = `/api/search_vacancies.php?page=${page}&limit=20&lang=${i18n.language}`;

          // Добавляем поисковый запрос
          if (query && !/^\d+$/.test(query)) {
            url += `&q=${encodeURIComponent(query)}`;
          }

          // Отделяем регионы от других категорий
          const selectedRegions = selectedCategories.filter((cat) =>
            regions.includes(cat),
          );

          // Все остальные категории (кроме регионов и специальных)
          const otherCategories = selectedCategories.filter(
            (cat) =>
              !regions.includes(cat) &&
              ![
                highestPaying,
                lastVacancies,
                topVacancies,
                t("undefined"),
                t("remote"),
                t("salaryDefined"),
                t("allcountryusers"),
                t("disabilitiesusers"),
              ].includes(cat),
          );

          // Добавляем регионы в URL
          if (selectedRegions.length > 0) {
            url += `&region=${encodeURIComponent(selectedRegions.join(","))}`;
          }

          // Добавляем остальные категории
          // Добавляем остальные категории
          if (otherCategories.length > 0) {
            url += `&category=${encodeURIComponent(otherCategories.join(","))}`;
          }

          // Добавляем специальные фильтры КАК КАТЕГОРИИ (не как workplace!)
          if (selectedCategories.includes(t("undefined"))) {
            url += `&category=Չսահմանված`;
          }

          if (selectedCategories.includes(t("remote"))) {
            url += `&category=${encodeURIComponent(t("remote"))}`;
          }

          if (selectedCategories.includes(t("salaryDefined"))) {
            url += `&category=${encodeURIComponent(t("salaryDefined"))}`;
          }

          if (selectedCategories.includes(t("allcountryusers"))) {
            url += `&foreigners=1`;
          }

          if (selectedCategories.includes(t("disabilitiesusers"))) {
            url += `&disabilities=1`;
          }

          // ИСПРАВЛЕННАЯ ЛОГИКА СПЕЦИАЛЬНЫХ ФИЛЬТРОВ
          // Для "Չսահմանված" (зарплата не указана)
          if (selectedCategories.includes(t("undefined"))) {
            url += `&workplace=Չսահմանված`;
          }

          // Для "հեռավար(ֆրիլանս)" (удаленная работа)
          if (selectedCategories.includes(t("remote"))) {
            url += `&workplace=հեռավար(ֆրիլանս)`;
          }

          // Для зарплаты определена
          if (selectedCategories.includes(t("salaryDefined"))) {
            url += `&salary_defined=1`;
          }

          if (selectedCategories.includes(t("allcountryusers"))) {
            url += `&foreigners=1`;
          }

          if (selectedCategories.includes(t("disabilitiesusers"))) {
            url += `&disabilities=1`;
          }
        }

        console.log("Запрос к API:", url);

        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        if (data.success) {
          setResults(data.vacancies);
          setTotalPages(data.totalPages || 1);
        } else {
          setResults([]);
        }
      } catch (error) {
        console.error("❌ Fetch error:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    if (!/^\d+$/.test(query)) fetchResults();
  }, [query, selectedCategories, page, i18n.language, t]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleFilterToggle = (label) => {
    setSelectedCategories((prev) => {
      const isSelected = prev.includes(label);
      if (isSelected) {
        return prev.filter((item) => item !== label);
      } else {
        return [...prev, label];
      }
    });
  };

  const handleApplyFilters = () => {
    setSearchParams(() => {
      const params = new URLSearchParams();
      if (inputValue) params.set("q", inputValue);
      if (selectedCategories.length > 0)
        params.set("category", selectedCategories.join(","));
      return params;
    });
    setPage(1);
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSearchParams(() => {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      return params;
    });
    setPage(1);
  };

  const handleSearchButtonClick = () => {
    setSearchParams(() => {
      const params = new URLSearchParams();
      params.set("q", inputValue);
      if (selectedCategories.length > 0)
        params.set("category", selectedCategories.join(","));
      return params;
    });
    setPage(1);
  };

  return (
    <section className="bg-white overflow-hidden w-full pb-3">
      {modalMessage && (
        <Modal
          title={modalMessage.title}
          text={modalMessage.text}
          ok={modalMessage.ok}
          success={modalMessage.success}
          onClose={() => {
            if (modalMessage.onClose) modalMessage.onClose();
            setModalMessage(null);
          }}
        />
      )}

      <section className="flex gap-6 max-lg:flex-col p-6 max-2xl:p-3 bg-gray-50">
        {showMobileFilters && (
          <div
            className="fixed inset-0 h-max bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setShowMobileFilters(false)}
          />
        )}

        <aside
          className={`
    w-[320px] max-2xl:w-[280px] max-sm:w-full bg-white p-4 rounded-2xl shadow-lg
    lg:block
    ${
      showMobileFilters
        ? "z-[999] rounded-none pb-32 h-screen overflow-y-auto block"
        : "hidden"
    }
  `}
        >
          {showMobileFilters && (
            <div className="flex justify-between items-center mb-4 pb-2 border-b">
              <h2 className="font-bold text-xl text-[#006674]">
                {t("filters")}
              </h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          )}

          <div className="flex justify-start gap-2 items-center mb-3">
            {!showMobileFilters && (
              <h2 className="font-bold text-xl text-[#006674]">
                {t("filters")}
              </h2>
            )}
            {selectedCategories.length > 0 && (
              <div>
                <button
                  onClick={() => {
                    handleApplyFilters();
                    setShowMobileFilters(false);
                  }}
                  className="w-full bg-[#006674] text-white py-1 px-2 rounded-lg font-semibold hover:bg-[#005662] transition-colors flex items-center justify-center gap-2"
                >
                  {t("searchtext")} ({selectedCategories.length})
                </button>
              </div>
            )}
            {selectedCategories.length > 0 && (
              <button
                onClick={() => {
                  handleClearFilters();
                  setShowMobileFilters(false);
                }}
                className="bg-red-500 text-white py-1 px-2 rounded-lg font-semibold"
              >
                {t("delete")}
              </button>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">{regionsTitle}</h3>
            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
              {regions.map((regionName, index) => (
                <label
                  key={index}
                  className="flex items-start gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(regionName)}
                    onChange={() => handleFilterToggle(regionName)}
                    className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">{regionName}</span>
                </label>
              ))}
            </div>
          </div>

          <hr className="border-[#006674] my-4" />
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">
                {t("EmploymentType")}
              </h3>
              {[
                t("Permanent"),
                t("Temporary"),
                t("thecontract"),
                t("Other"),
              ].map((item, i) => (
                <label
                  key={i}
                  className="flex items-start  gap-2 mb-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(item)}
                    onChange={() => handleFilterToggle(item)}
                    className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>

            <hr className="border-[#006674]" />

            <div>
              <h3 className="font-semibold text-gray-800 mb-2">
                {t("EmploymentTime")}
              </h3>
              {[t("Full-time"), t("Part-time"), t("Other")].map((item, i) => (
                <label
                  key={i}
                  className="flex items-start gap-2 mb-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(item)}
                    onChange={() => handleFilterToggle(item)}
                    className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
            <hr className="border-[#006674]" />

            <div>
              <h3 className="font-semibold text-gray-800 mb-2">
                {t("Experience")}
              </h3>
              {[
                t("no"),
                t("beginner"),
                t("mid"),
                t("senior"),
                t("undefined"),
              ].map((item, i) => (
                <label
                  key={i}
                  className="flex items-start gap-2 mb-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(item)}
                    onChange={() => handleFilterToggle(item)}
                    className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
            <hr className="border-[#006674]" />

            <div>
              <label className="flex items-start gap-2 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(t("remote"))}
                  onChange={() => handleFilterToggle(t("remote"))}
                  className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                />
                <span>{t("remote")}</span>
              </label>
              <label className="flex items-start gap-2 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(t("salaryDefined"))}
                  onChange={() => handleFilterToggle(t("salaryDefined"))}
                  className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                />
                <span>{t("salaryDefined")}</span>
              </label>
            </div>
            <hr className="border-[#006674]" />
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">
                {t("industry")}
              </h3>
              <ul className="flex flex-col gap-2 h-auto">
                {filtres.map((label, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id={`filter-${index}`}
                      checked={selectedCategories.includes(label)}
                      onChange={() => handleFilterToggle(label)}
                      className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                    />
                    <label
                      htmlFor={`filter-${index}`}
                      className="text-sm text-gray-700 cursor-pointer"
                    >
                      {label}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            <hr className="border-[#006674]" />

            <div>
              <h3 className="font-semibold text-gray-800 mb-2">
                {t("AlsoConsidered")}
              </h3>
              {[t("allcountryusers"), t("disabilitiesusers")].map((item, i) => (
                <label
                  key={i}
                  className="flex items-start gap-2 mb-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(item)}
                    onChange={() => handleFilterToggle(item)}
                    className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
            <hr className="border-[#006674]" />

            <div>
              <h3 className="font-semibold text-gray-800 mb-2">{t("other")}</h3>
              {[
                t("topvacansies"),
                t("Highestpayingjobs"),
                t("lastvacansies"),
              ].map((item, i) => (
                <label
                  key={i}
                  className="flex items-start gap-2 mb-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(item)}
                    onChange={() => handleFilterToggle(item)}
                    className="accent-[#006674] min-w-[20px] min-h-[20px] translate-y-[2px] cursor-pointer"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>
          <hr className="border-[#006674] mt-3" />
        </aside>

        <main className="flex-1 px-12 max-2xl:px-3 flex flex-col gap-4">
          <div className="flex gap-3 items-center">
            <div className="w-full bg-white shadow-md rounded-full transition-all p-2 max-md:p-2 border-2 border-transparent flex items-center gap-3 group focus-within:border-[#006674]">
              <input
                type="text"
                placeholder={t("searchtext")}
                value={inputValue}
                onChange={handleSearch}
                className="flex-1 outline-none pl-6 text-gray-700 bg-transparent"
              />

              {inputValue && (
                <button
                  onClick={() => {
                    setInputValue("");
                    setSearchParams({});
                    setSelectedCategories([]);
                    setPage(1);
                  }}
                  className="w-[30px] h-[30px] flex items-center justify-center text-gray-500 text-lg hover:text-red-500 transition-colors"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}

              <button
                className="bg-[#006674] w-[40px] h-[40px] rounded-full flex justify-center items-center hover:bg-[#005662] transition-colors"
                onClick={handleSearchButtonClick}
              >
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-white text-lg"
                />
              </button>
            </div>

            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="bg-[#006674] lg:hidden min-w-[40px] min-h-[40px] p-2  max-lg:flex justify-center items-center text-white rounded-full shadow-lg hover:bg-[#005662] transition-colors"
            >
              <img src={filtericon} className="size-5 object-contain" alt="" />
            </button>
          </div>

          {selectedCategories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedCategories.map((category, index) => (
                <span
                  key={index}
                  className="bg-[#006674] text-white px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  {category}
                  <button
                    onClick={() => handleFilterToggle(category)}
                    className="text-white hover:text-gray-200 text-xs"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-[200px]">
              <p className="text-[#006674] font-semibold animate-pulse text-xl">
                {t("loading")}...
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-10 text-gray-600 text-xl max-md:text-sm">
              {t("noresults")}
            </div>
          ) : (
            <>
              <h1 className="text-2xl max-md:text-xl font-bold text-[#006674] mb-2">
                {t("result")}:{" "}
                <span>"{inputValue || selectedCategories.join(", ")}"</span>
              </h1>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-5">
                {results.map((vacancy) => {
                  const isLiked = likedVacancies.includes(Number(vacancy.id));
                  return (
                    <article
                      key={vacancy.id}
                      className="w-full pt-3 overflow-hidden flex-col relative flex justify-start gap-3 items-center rounded-lg shadow-lg bg-white"
                    >
                      <div className="w-full px-3 flex justify-start items-center gap-6">
                        <img
                          className="w-[120px] h-[120px] max-md:w-[90px] max-md:h-[90px]  ml-6 max-md:ml-0 rounded-xl border-[3px] border-white bg-gray-200 object-contain"
                          src={`/api//uploads/${vacancy.companyLogo}`}
                          alt={vacancy.companyName || "Company Logo"}
                        />
                        <ul className="flex flex-col gap-1 items-start justify-center">
                          <li>
                            <Link
                              to={`/vacancy/${vacancy.id}`}
                              className="text-2xl max-md:text-[16px] text-[#006674] font-semibold hover:underline"
                            >
                              {vacancy.workname}
                            </Link>
                          </li>
                          <li className="text-gray-800 text-xl max-md:text-sm">
                            {vacancy.companyName}
                          </li>
                          <li className="flex gap-2 items-center text-gray-600 text-md max-md:text-sm">
                            <FontAwesomeIcon icon={faLocationDot} />
                            {vacancy.workplace}
                          </li>
                          <li className="flex gap-2 text-md items-center max-md:text-sm text-gray-600 ">
                            <FontAwesomeIcon icon={faMoneyBill1} />
                            {vacancy.salary
                              ? `${vacancy.salary} ${vacancy.currency || ""}`
                              : t("notspecified")}
                          </li>
                        </ul>

                        <button
                          onClick={() => handleLike(vacancy.id)}
                          className={`absolute right-2 top-24 rounded-full border-[2px] border-white shadow-lg shadow-neutral-500 transition-all duration-200 w-[35px] h-[35px] ${
                            isLiked
                              ? "bg-red-500 text-white"
                              : "bg-gray-400 text-white hover:bg-[#006674]"
                          } ${user?.role === "company" ? "pointer-events-none opacity-60" : ""}`}
                        >
                          <FontAwesomeIcon icon={faHeart} />
                        </button>
                      </div>
                      <div className="w-full mt-2 p-2 flex justify-between items-center bg-[#006674] text-white text-sm">
                        <span>
                          <FontAwesomeIcon
                            className="mr-1"
                            icon={faCalendarAlt}
                          />
                          {new Date(vacancy.created_at).toLocaleDateString()}
                        </span>
                        <Link
                          to={`/vacancy/${vacancy.id}`}
                          className="hover:underline"
                        >
                          {t("seemore")}
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="flex gap-3 mt-6 justify-center items-center">
                  <button
                    onClick={() => page > 1 && setPage(page - 1)}
                    disabled={page === 1}
                    className={`px-4 py-2 rounded-lg ${
                      page === 1
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "bg-[#006674] text-white hover:bg-[#00555a]"
                    } transition`}
                  >
                    <FontAwesomeIcon icon={faAngleLeft} />
                  </button>

                  <span className="text-[#006674] font-semibold">
                    {page} / {totalPages}
                  </span>

                  <button
                    onClick={() => page < totalPages && setPage(page + 1)}
                    disabled={page === totalPages}
                    className={`px-4 py-2 rounded-lg ${
                      page === totalPages
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "bg-[#006674] text-white hover:bg-[#00555a]"
                    } transition`}
                  >
                    <FontAwesomeIcon icon={faAngleRight} />
                  </button>
                </div>
              )}
            </>
          )}
        </main>

        <aside className="w-[400px] max-md:w-full flex flex-col gap-4">
          <TopVakanciesSearch />
        </aside>
      </section>

      {showMobileFilters && selectedCategories.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4 border-t shadow-lg lg:hidden">
          <button
            onClick={() => {
              handleApplyFilters();
              setShowMobileFilters(false);
            }}
            className="w-full bg-[#006674] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#005662] transition-colors"
          >
            {t("searchtext")} ({selectedCategories.length})
          </button>
        </div>
      )}

      <SearchPageBanners />
    </section>
  );
};

export default SearchResults;
